/*
  <gesture-element> — vertical touch pan detection, plus the swipe-down-to-dismiss
  behaviour shared by the menu drawer and the cart drawer. Ported from Concept's
  GestureElement and the pan handlers on its ModalElement.

  Put it on the sheet's header (Concept does), give it tabindex="0", then wire it
  up from the drawer that owns it:

    gesture.enableDismiss({
      wrap: panelElement,          // the sheet that follows the finger
      overlay: overlayElement,     // fades out as the sheet travels down
      isOpen: () => drawer.open,
      onDismiss: () => drawer.close()
    });

  Call resetDismiss() when the drawer closes, to drop the inline styles the drag
  left behind. Drag past 30% of the sheet's height and it lets go; anything less
  springs back.
*/
(function () {
  if (customElements.get('gesture-element')) return;

  /* Resolves on `el`'s transitionend, or after `fallback` ms if it never fires
     (an interrupted transition, or one that never had anything to animate). */
  function afterTransition(el, fallback) {
    return new Promise(function (resolve) {
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', onEnd);
        resolve();
      };
      var onEnd = function (event) {
        if (event.target === el) finish();
      };
      el.addEventListener('transitionend', onEnd);
      setTimeout(finish, fallback);
    });
  }

  function clamp(a, min, max) {
    return Math.min(max, Math.max(min, a));
  }

  function invlerp(x, y, a) {
    return clamp((a - x) / (y - x), 0, 1);
  }

  customElements.define(
    'gesture-element',
    class GestureElement extends HTMLElement {
      constructor() {
        super();
        this.handlers = { panstart: [], panmove: [], panend: [] };
      }

      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
        this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      on(type, fn) {
        if (this.handlers[type]) this.handlers[type].push(fn);
      }

      fire(type, event) {
        this.handlers[type].forEach(function (fn) {
          fn(event);
        });
      }

      onTouchStart(event) {
        this.touchStartY = event.changedTouches[0].screenY;
        this.touchMoveY = null;
        this.fire('panstart', event);
      }

      onTouchMove(event) {
        var touchMoveY = event.changedTouches[0].screenY - (this.touchStartY || 0);
        this.velocityY = touchMoveY - (this.touchMoveY || 0);
        this.touchMoveY = touchMoveY;
        this.fire('panmove', event);
      }

      onTouchEnd(event) {
        this.fire('panend', event);
      }

      /* ---- swipe to dismiss ---- */
      enableDismiss(options) {
        if (this.dismiss) return;
        this.dismiss = {
          wrap: options.wrap,
          overlay: options.overlay,
          isOpen: options.isOpen,
          onDismiss: options.onDismiss,
          animationFrame: null,
          layerHeight: 0,
          maxGestureDistance: 0,
          endPoint: 0,
          moveY: 0
        };

        this.on('panstart', this.onDismissStart.bind(this));
        this.on('panmove', this.onDismissMove.bind(this));
        this.on('panend', this.onDismissEnd.bind(this));
      }

      resetDismiss() {
        var d = this.dismiss;
        if (!d) return;
        d.wrap.style.transform = '';
        d.wrap.style.transition = '';
        d.overlay.style.opacity = '';
        d.overlay.style.transition = '';
      }

      onDismissStart() {
        var d = this.dismiss;
        if (!d.isOpen()) return;
        d.wrap.style.transition = '';
        d.layerHeight = d.wrap.getBoundingClientRect().height;
        d.maxGestureDistance = d.layerHeight - 50;
        d.endPoint = d.layerHeight * 0.3;
      }

      onDismissMove() {
        var d = this.dismiss;
        if (!d.isOpen() || d.animationFrame) return;

        d.animationFrame = requestAnimationFrame(function () {
          d.wrap.style.transition = 'transform .1s linear';
          d.overlay.style.transition = 'opacity .1s linear';
          d.moveY = this.touchMoveY;

          if (d.moveY >= 0) {
            d.wrap.style.transform = 'translateY(' + Math.min(d.moveY, d.maxGestureDistance) + 'px)';
            d.overlay.style.opacity = invlerp(d.layerHeight, 0, d.moveY);
          } else {
            // Dragging up past the top just pins the sheet in place.
            d.wrap.style.transform = 'translateY(0)';
            d.wrap.style.transition = '';
            d.overlay.style.opacity = '1';
          }

          d.animationFrame = null;
        }.bind(this));
      }

      onDismissEnd() {
        var d = this.dismiss;
        if (d.animationFrame) cancelAnimationFrame(d.animationFrame);
        d.animationFrame = null;
        if (!d.isOpen()) return;

        d.wrap.style.transition = 'transform .1s linear';
        d.moveY = this.touchMoveY || 0;

        // Past 30% of the sheet's height, let go — otherwise spring back.
        if (d.moveY < d.endPoint) {
          d.wrap.style.transform = 'translateY(0)';
          return;
        }

        d.wrap.style.transform = 'translateY(' + d.layerHeight + 'px)';
        afterTransition(d.wrap, 300).then(d.onDismiss);
      }
    }
  );
})();
